import billingApi from 'api/billing-api';
import {
  fetchAccessToken,
  getAccessToken,
  getRegistrationToken,
  getTokenValidationTime,
} from 'api/oauth-api';
import { UPDATE_TOKEN_TIME } from 'data/constants';

interface TokenObject {
  token: string;
  durationSeconds: number;
}

interface BCS {
  setTokenObj: (tokenObj: TokenObject | null) => void;
  setAccessToken: (accessToken: string | null) => void;
  setRegistrationToken: (registrationToken: string | null) => void;
}

const fetchTokens = async (
  bcs: BCS | null = null,
): Promise<{
  tokenObj: TokenObject | null;
  accessToken: string | null;
  registrationToken: string | null;
  wsServerUrl: string | null;
}> => {
  let tokenObj: TokenObject | null = null;
  let accessToken: string | null = null;
  let registrationToken: string | null = null;
  let wsServerUrl: string | null = null;

  try {
    const [billingResult, accessTokenResult] = await Promise.allSettled([
      billingApi('/token'),
      getAccessToken(),
    ]);

    if (billingResult.status === 'fulfilled') {
      tokenObj = billingResult.value as TokenObject;
    } else {
      console.error('Error when receiving old token:', billingResult.reason);
    }

    if (accessTokenResult.status === 'fulfilled') {
      accessToken = accessTokenResult.value as string;
      try {
        const registrationResult = await getRegistrationToken();
        [registrationToken, wsServerUrl] = registrationResult as [
          string | null,
          string | null,
          string | null,
        ];
      } catch (error) {
        console.error('Error when receiving registration token:', error);
      }
    } else {
      console.error('Error when receiving OAuth tokens:', accessTokenResult.reason);
    }
  } catch (error) {
    console.error('Unexpected error:', error);
  }

  if (bcs) {
    const { setTokenObj, setAccessToken, setRegistrationToken } = bcs;
    setTokenObj(tokenObj);
    setAccessToken(accessToken);
    setRegistrationToken(registrationToken);
  }

  return { tokenObj, accessToken, registrationToken, wsServerUrl };
};

const refreshTokens = async (
  bcs: BCS,
): Promise<{
  tokenObj: TokenObject | null;
  accessToken: string | null;
  registrationToken: string | null;
  wsServerUrl: string | null;
}> => {
  let tokens = await fetchTokens(bcs);
  setInterval(async () => {
    tokens = await fetchTokens(bcs);
  }, UPDATE_TOKEN_TIME);

  return tokens;
};

const chooseServer = async (
  serverList: string[],
): Promise<[string, Record<string, string | null>]> => {
  const serverListResults = await Promise.allSettled(
    serverList.map((server) => fetchAccessToken(server)),
  );

  const resultsTokens: Record<string, string | null> = {};
  serverList.forEach((server, index) => {
    const result = serverListResults[index];
    if (result.status === 'fulfilled') {
      const [access_token, refresh_token] = result.value;
      resultsTokens[server] = access_token;
    }
  });

  let bestServer: string | null = null;
  let minValidationTime = Infinity;
  for (const [server, token] of Object.entries(resultsTokens)) {
    if (token) {
      try {
        const validationTime = await getTokenValidationTime(server, token);
        if (validationTime < minValidationTime) {
          minValidationTime = validationTime;
          bestServer = server;
        }
      } catch (error) {
        console.error(`Failed to validate token for server: ${server}`, error);
      }
    }
  }

  if (bestServer) {
    return [bestServer, resultsTokens];
  }

  return ['', null];
};

export { refreshTokens, chooseServer };

export type { BCS, TokenObject };
